import { Button, Form, type FormProps, Modal, Typography, Select, Flex } from 'antd'
import React, { useState } from 'react'

import { SchoolService, type UpdateUserBody } from '@services/SchoolService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { SchoolSubscription, SchoolUser } from '../../../../types'

type ModalProps = {
    schoolUser: SchoolUser
    handleClose: () => void
    onSuccess: () => Promise<void>
    availableSchoolSubscriptions: SchoolSubscription[]
}

type FieldType = {
    role?: string
    email?: string
    schoolSubscriptionIds?: string[]
}

export const EditSchoolUserModal: React.FC<ModalProps> = ({
    handleClose,
    onSuccess,
    schoolUser,
    availableSchoolSubscriptions: rawAvailableSchoolSubscriptions
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const confirm = useConfirmation()

    const { showNotification } = useNotifications()

    const onFinish: FormProps<FieldType>['onFinish'] = async values => {
        const currentlyHave = schoolUser.schoolSubscriptionSeats
        const seatsToSubmit = values.schoolSubscriptionIds ?? []

        // determine seats to be added
        const toBeAdded = seatsToSubmit.filter(ssId => !currentlyHave.find(sss => sss.schoolSubscriptionId === ssId))

        let notifyOnSeatAddition = false

        if (toBeAdded.length > 0) {
            notifyOnSeatAddition = await confirm({
                title: 'Notify the user of this change?',
                description:
                    'A subscription is being added to the user. Do you want to notify the user that their subscription is active?',
                confirmText: 'Yes',
                cancelText: 'No'
            })
        }

        const body: UpdateUserBody = {
            role: values.role!,
            schoolSubscriptionIds: values.schoolSubscriptionIds ?? [],
            notifyOnSeatAddition
        }

        try {
            setIsLoading(true)
            await SchoolService.updateUser(schoolUser.schoolId, schoolUser.user.id, body)
            showNotification({
                title: 'User has been updated'
            })

            await onSuccess()
            handleClose()
        } catch (err: any) {
            showNotification({
                title: 'User updated has failed',
                type: 'error'
            })
        } finally {
            setIsLoading(false)
        }
    }

    if (!schoolUser) {
        return null
    }

    const availableSchoolSubscriptions = rawAvailableSchoolSubscriptions.filter(
        ss => ss.subscription.status !== 'CANCELLED' && ss.subscription.status !== 'COMPLETED'
    )

    // const availableSchoolSubscriptions = rawAvailableSchoolSubscriptions

    return (
        <Modal open={!!schoolUser} footer={[]} onCancel={handleClose}>
            <Flex vertical gap="middle">
                <Typography.Title level={4}>Edit User - {schoolUser.user.email}</Typography.Title>
                <Form
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 14 }}
                    layout="horizontal"
                    disabled={false}
                    onFinish={onFinish}
                >
                    <Form.Item label="Role" name="role" initialValue={schoolUser.role}>
                        <Select defaultValue="USER" allowClear={false}>
                            <Select.Option value="USER">User</Select.Option>
                            <Select.Option value="ADMIN">Admin</Select.Option>
                            <Select.Option value="PARENT">Parent</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="Subscriptions"
                        name="schoolSubscriptionIds"
                        initialValue={
                            schoolUser.schoolSubscriptionSeats
                                .filter(sss =>
                                    availableSchoolSubscriptions.find(ass => ass.id === sss.schoolSubscriptionId)
                                )
                                .map(sss => sss.schoolSubscriptionId) || []
                        }
                    >
                        <Select
                            style={{ minWidth: '300px' }}
                            mode="multiple"
                            options={availableSchoolSubscriptions.map(s => ({
                                label:
                                    s.subscription.name ||
                                    s.subscription.subscriptionProducts.map(sp => sp.product.key).join(', '),
                                value: s.id,
                                color: 'green'
                            }))}
                        />
                    </Form.Item>
                    <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                        <Button type="primary" htmlType="submit" loading={isLoading}>
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Flex>
        </Modal>
    )
}
